import React from "react";
import { Link } from "react-router-dom";
import { Waves, ArrowRight } from "lucide-react";

/**
 * OceanCollectionStrip — subtle homepage promo for the Ocean Collection.
 * Sits under the ChunkyCuffsBanner to introduce ocean-themed pieces without
 * dominating the flow. Warm cream + soft teal wash matches Soft Luxe.
 */
const OceanCollectionStrip = () => (
  <section
    className="py-10 lg:py-14"
    style={{ background: "#FDF6F0" }}
    data-testid="ocean-collection-strip"
  >
    <div className="max-w-[1240px] mx-auto px-5 lg:px-10">
      <Link
        to="/shop?category=ocean"
        className="group relative flex flex-col md:flex-row items-stretch rounded-[24px] overflow-hidden border transition-all hover:shadow-[0_28px_50px_-24px_rgba(74,44,53,0.35)] hover:-translate-y-0.5"
        style={{
          background:
            "linear-gradient(115deg, #EAF2EE 0%, #F4EEE6 55%, #FBEAF0 100%)",
          borderColor: "rgba(197,152,90,0.35)",
        }}
        data-testid="ocean-strip-link"
      >
        {/* Soft wave overlay */}
        <span
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 200'><path d='M0 120 Q 300 60 600 120 T 1200 120 L 1200 200 L 0 200 Z' fill='rgba(120,160,155,0.15)'/><path d='M0 150 Q 300 100 600 150 T 1200 150 L 1200 200 L 0 200 Z' fill='rgba(197,152,90,0.12)'/></svg>")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />

        {/* LEFT: Copy */}
        <div className="relative z-10 flex-1 px-7 sm:px-10 lg:px-14 py-9 lg:py-12 flex flex-col justify-center">
          <p
            className="text-[10.5px] tracking-[0.42em] uppercase font-semibold mb-2.5 inline-flex items-center gap-2"
            style={{ color: "#7A8F87" }}
          >
            <Waves size={13} strokeWidth={2} /> New Collection
          </p>
          <h2
            className="font-serif text-plum leading-[1.02] tracking-tight"
            style={{ fontSize: "clamp(30px, 4.2vw, 52px)" }}
          >
            Ocean <span className="italic text-blush">Collection</span>
          </h2>
          <p
            className="mt-3 max-w-[440px] text-plum/70 font-serif italic"
            style={{ fontSize: "clamp(14px, 1.35vw, 17px)" }}
          >
            Sun-touched gold, shells, and Riviera stories — jewels for the
            days you feel infinite.
          </p>
          <span
            className="inline-flex items-center gap-1.5 mt-6 text-[11px] tracking-[0.28em] uppercase font-semibold text-plum group-hover:text-blush transition-colors"
          >
            Explore the Edit
            <ArrowRight
              size={13}
              strokeWidth={2.4}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </span>
        </div>

        {/* RIGHT: Editorial image */}
        <div className="relative md:w-[42%] lg:w-[40%] aspect-[16/9] md:aspect-auto overflow-hidden">
          <img
            src="/categories/ocean.png"
            alt="Ocean Collection"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
            loading="lazy"
            draggable="false"
            onError={(e) => {
              e.currentTarget.src = "/reels/gg5.png";
            }}
          />
          {/* Warm side fade so the copy is legible where it meets the image on mobile */}
          <div
            className="absolute inset-0 md:hidden pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, transparent 55%, rgba(74,44,53,0.35) 100%)",
            }}
          />
        </div>
      </Link>
    </div>
  </section>
);

export default OceanCollectionStrip;
